/**
 * member-merge-form.tsx — Confirmation form for member record merge.
 *
 * Receives pre-selected retainedMemberId and deprecatedMemberId from the
 * MemberMergePage (after user searches and selects records). Renders a
 * confirmation step showing the two IDs and a confirm button.
 *
 * PHI constraint: Only internal ULIDs are displayed (no name, DOB, or MBI).
 * The merge API payload contains ULIDs only.
 *
 * Roles: staff:admin or staff:wa (enforced by route guard in app.tsx).
 */

import type { JSX } from 'react';

import { useMemberMerge } from './use-member-merge.js';

export interface MemberMergeFormProps {
  retainedMemberId: string;
  deprecatedMemberId: string;
  onComplete?: () => void;
}

export function MemberMergeForm({
  retainedMemberId,
  deprecatedMemberId,
  onComplete,
}: MemberMergeFormProps): JSX.Element {
  const { merge, isPending, isSuccess, error } = useMemberMerge();

  async function handleConfirm(): Promise<void> {
    await merge({ retainedMemberId, deprecatedMemberId });
    if (onComplete !== undefined) {
      onComplete();
    }
  }

  if (isSuccess) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-6">
        <p className="font-medium text-green-800">Merge complete</p>
        <p className="mt-1 text-sm text-green-700">
          Member records have been merged successfully. The deprecated record is now inactive.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-medium text-gray-900">Confirm member merge</h2>
        <p className="mt-1 text-sm text-gray-500">
          This action is irreversible. The deprecated record will be marked inactive and all
          enrollment history will be transferred to the retained record.
        </p>

        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between rounded border border-green-200 bg-green-50 px-4 py-3">
            <span className="text-sm font-medium text-green-800">Retained record</span>
            <span className="font-mono text-sm text-green-900">{retainedMemberId}</span>
          </div>

          <div className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-4 py-3">
            <span className="text-sm font-medium text-red-800">Deprecated record</span>
            <span className="font-mono text-sm text-red-900">{deprecatedMemberId}</span>
          </div>
        </div>

        {error !== null && (
          <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm font-medium text-red-800">Merge failed</p>
            <p className="mt-1 text-sm text-red-700">{error.message}</p>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={isPending}
          onClick={(): void => {
            void handleConfirm();
          }}
          className="rounded-md bg-red-600 px-6 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Confirm merge"
        >
          {isPending ? 'Merging…' : 'Confirm merge'}
        </button>
      </div>
    </div>
  );
}
